import {
  type DistributionShare,
  type EstateReadinessInput,
  EstateReadinessInputSchema,
  type EstateReadinessOutput,
  type IntestacyInput,
  IntestacyInputSchema,
  type IntestacyOutput,
  type IntestacyRuleSet,
  type ReadinessItem,
  type StatutoryLegacyPeriod,
} from "./types.js";

// ---------------------------------------------------------------------------
// Regulatory guards (ship-conditions, hard-encoded — panels/intestacy-checker.md
// §2). These are EXECUTABLE rules, not doc prose, each with a test.
//
//  (1) ESTATE-ONLY: the ruleset answers "who inherits your estate under
//      intestacy" and stops. Super death benefits, testamentary trusts and
//      life-insurance proceeds are `s764A`/financial-product-adjacent and must
//      NEVER be modelled here — modelling them next to a paid handoff is the
//      AFSL kill-line (contra-regulatory C2). `assertEstateOnly` throws for any
//      such identifier reaching a distribution surface.
//
//  (2) FACTS-ONLY: NO affiliate / will-provider identifier may reach this
//      module — the rules emit facts only; the Safewill/Willed handoff lives off
//      the calc surface (in app/marketing). `assertNoAffiliate` enforces it.
// ---------------------------------------------------------------------------

/** Financial-product / estate-adjacent topics that must never be modelled here. */
const FORBIDDEN_FINANCIAL_PRODUCT_PATTERNS = [
  "super",
  "superannuation",
  "death benefit",
  "binding nomination",
  "binding death",
  "testamentary trust",
  "life insurance",
  "life-insurance",
  "insurance proceeds",
  "smsf",
  "pension",
  "annuity",
];

/** Will-provider / affiliate identifiers that may never ride a calc surface. */
const FORBIDDEN_AFFILIATE_PATTERNS = [
  "safewill",
  "willed",
  "commission factory",
  "commissionfactory",
  "affiliate",
  "referral fee",
  "lead fee",
];

export class FinancialProductError extends Error {
  constructor(identifier: string) {
    super(
      `estate-only guard: "${identifier}" is a financial-product / estate-adjacent ` +
        `topic (super, testamentary trust, life insurance) and must not be modelled on an ` +
        `intestacy distribution surface (panels/intestacy-checker.md §2; contra-regulatory C2).`,
    );
    this.name = "FinancialProductError";
  }
}

export class AffiliateOnSurfaceError extends Error {
  constructor(identifier: string) {
    super(
      `facts-only guard: "${identifier}" is a will-provider / affiliate identifier and must ` +
        `not reach the rules module — the handoff lives off the calc surface (panels/intestacy-checker.md §2).`,
    );
    this.name = "AffiliateOnSurfaceError";
  }
}

/** Throw if `identifier` names a financial product the intestacy surface must not model. */
export function assertEstateOnly(identifier: string): string {
  const id = identifier.toLowerCase();
  for (const p of FORBIDDEN_FINANCIAL_PRODUCT_PATTERNS) {
    if (id.includes(p)) throw new FinancialProductError(identifier);
  }
  return identifier;
}

/** Throw if `identifier` names a will-provider / affiliate. The rules emit facts only. */
export function assertNoAffiliate(identifier: string): string {
  const id = identifier.toLowerCase();
  for (const p of FORBIDDEN_AFFILIATE_PATTERNS) {
    if (id.includes(p)) throw new AffiliateOnSurfaceError(identifier);
  }
  return identifier;
}

// ---------------------------------------------------------------------------
// Statutory-legacy resolution (date-keyed).
// ---------------------------------------------------------------------------

/**
 * The statutory legacy in force on an ISO date. Periods are authored newest
 * first; picks the most recent whose `[effectiveFrom, effectiveTo]` window
 * contains the date (open `effectiveTo` of `null` extends to "now"). Throws if
 * the date predates every period.
 */
export function statutoryLegacyForDate(
  rules: IntestacyRuleSet,
  isoDate: string,
): StatutoryLegacyPeriod {
  for (const period of rules.statutoryLegacy) {
    const afterStart = isoDate >= period.effectiveFrom;
    const beforeEnd = period.effectiveTo === null || isoDate <= period.effectiveTo;
    if (afterStart && beforeEnd) return period;
  }
  throw new Error(
    `no ${rules.state} statutory legacy registered for date ${isoDate}`,
  );
}

// ---------------------------------------------------------------------------
// intestacyDistribution
// ---------------------------------------------------------------------------

/** A share with no dollar amount (resolved later when estateValue is known). */
function share(
  to: DistributionShare["to"],
  opts: {
    personalEffects?: boolean;
    statutoryLegacy?: number;
    residueFraction?: number;
  } = {},
): DistributionShare {
  return {
    to,
    personalEffects: opts.personalEffects ?? false,
    statutoryLegacy: opts.statutoryLegacy ?? 0,
    residueFraction: opts.residueFraction ?? 0,
    amount: null,
  };
}

/**
 * Compute the statutory intestacy distribution for a state + family structure on
 * an assessment date. Pure: `inputs -> rules -> outputs`, no I/O, no `Date.now()`.
 * Validates at the boundary. This recites the statutory default — it is
 * information, not advice.
 */
export function intestacyDistribution(
  rawInput: IntestacyInput,
  rules: IntestacyRuleSet,
): IntestacyOutput {
  const input = IntestacyInputSchema.parse(rawInput);
  if (input.state !== rules.state) {
    throw new Error(
      `state mismatch: input ${input.state} vs ruleset ${rules.state}`,
    );
  }

  const hasPartner = input.partner === "partner";
  const hasIssue = input.children !== "none";
  const childCount = input.childCount ?? (hasIssue ? 1 : 0);

  const legacyPeriod = statutoryLegacyForDate(rules, input.assessmentDate);
  const estate = input.estateValue;

  // The components we build up.
  let shares: DistributionShare[] = [];
  let statutoryLegacyUsed = 0;
  let partnerTakesAll = false;
  let blendedSplitApplies = false;
  let passesToCrown = false;

  if (hasPartner && !hasIssue) {
    // Partner, no issue → partner takes the whole estate (NSW s111, VIC s70J, QLD).
    shares = [share("partner", { personalEffects: true, residueFraction: 1 })];
    partnerTakesAll = true;
  } else if (hasPartner && hasIssue) {
    const allShared = input.children === "all-shared";
    if (allShared && rules.partnerTakesAllWhenAllShared) {
      // NSW s112 / VIC s70J — all issue are the partner's → partner takes all.
      shares = [share("partner", { personalEffects: true, residueFraction: 1 })];
      partnerTakesAll = true;
    } else {
      // The split: partner takes personal effects + statutory legacy + a fraction
      // of the residue; the children take the rest. Applies to NSW/VIC blended
      // families (s113 / s70M) and to QLD spouse+issue (shared OR other).
      statutoryLegacyUsed = legacyPeriod.amount;
      const partnerResidueShare =
        childCount <= 1 && rules.partnerResidueShareOneChild !== null
          ? rules.partnerResidueShareOneChild
          : rules.partnerResidueShare;

      // If the estate is at/below the statutory legacy, the partner takes all
      // (the legacy is more than the whole estate — the split never bites).
      if (estate !== undefined && estate <= statutoryLegacyUsed) {
        shares = [
          share("partner", { personalEffects: true, residueFraction: 1 }),
        ];
        partnerTakesAll = true;
        statutoryLegacyUsed = 0; // partner simply takes everything; legacy moot
      } else {
        shares = [
          share("partner", {
            personalEffects: true,
            statutoryLegacy: statutoryLegacyUsed,
            residueFraction: partnerResidueShare,
          }),
          share("children", { residueFraction: 1 - partnerResidueShare }),
        ];
        // The "surprise" only when there are NON-partner children.
        blendedSplitApplies = input.children === "some-other";
      }
    }
  } else if (!hasPartner && hasIssue) {
    // No partner, issue take the whole estate equally.
    shares = [share("children", { residueFraction: 1 })];
  } else {
    // No partner, no issue → parents, then siblings, then Crown (bona vacantia).
    if (input.kin === "parents") {
      shares = [share("parents", { residueFraction: 1 })];
    } else if (input.kin === "siblings") {
      shares = [share("siblings", { residueFraction: 1 })];
    } else {
      shares = [share("crown", { residueFraction: 1 })];
      passesToCrown = true;
    }
  }

  // Resolve dollar amounts when the estate value is known.
  if (estate !== undefined) {
    const residue = Math.max(0, estate - statutoryLegacyUsed);
    shares = shares.map((s) => ({
      ...s,
      amount: round2(s.statutoryLegacy + residue * s.residueFraction),
    }));
  }

  return {
    state: rules.state,
    act: rules.act,
    distributionRef: rules.distributionRef,
    statutoryLegacy: statutoryLegacyUsed,
    statutoryLegacyEffectiveFrom:
      statutoryLegacyUsed > 0 ? legacyPeriod.effectiveFrom : null,
    shares,
    partnerTakesAll,
    blendedSplitApplies,
    passesToCrown,
  };
}

/** Round to whole cents (statutory legacy carries cents). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---------------------------------------------------------------------------
// estateReadiness
// ---------------------------------------------------------------------------

// The factual state default if each document is missing — information, a prompt
// to act, NOT advice. We state what the law/State does in its absence; we never
// say "you should appoint X" or "buy this".
const DEFAULT_IF_MISSING = {
  will:
    "Without a valid will your estate is distributed under your state's intestacy rules — the State decides who inherits and who administers the estate, not you.",
  enduringPowerOfAttorney:
    "Without an enduring power of attorney, if you lose capacity no one (not even your partner) automatically controls your finances — a family member must apply to the state tribunal (NCAT / VCAT / QCAT) to be appointed.",
  enduringGuardianship:
    "Without an enduring guardianship / medical decision-maker, if you lose capacity the same tribunal decides who makes your health and lifestyle decisions.",
  guardianNomination:
    "Without a guardian nomination in a will, who raises your minor children is left to a court rather than chosen by you.",
} as const;

/**
 * Score readiness across the four documents that fall over at death or
 * incapacity, surfacing the factual state default behind each gap. Pure;
 * validates at the boundary. The output is a readiness PICTURE and a prompt to
 * act — deliberately not a "score out of 100" and never a product
 * recommendation.
 */
export function estateReadiness(
  rawInput: EstateReadinessInput,
): EstateReadinessOutput {
  const input = EstateReadinessInputSchema.parse(rawInput);

  const items: ReadinessItem[] = [];

  // Will — covered, stale (life event since), or gap.
  items.push({
    doc: "will",
    status: !input.hasWill
      ? "gap"
      : input.lifeEventSinceWill
        ? "stale"
        : "covered",
    defaultIfMissing: DEFAULT_IF_MISSING.will,
  });

  items.push({
    doc: "enduringPowerOfAttorney",
    status: input.hasEnduringPowerOfAttorney ? "covered" : "gap",
    defaultIfMissing: DEFAULT_IF_MISSING.enduringPowerOfAttorney,
  });

  items.push({
    doc: "enduringGuardianship",
    status: input.hasEnduringGuardianship ? "covered" : "gap",
    defaultIfMissing: DEFAULT_IF_MISSING.enduringGuardianship,
  });

  // Guardian nomination only applies if there are minor children.
  items.push({
    doc: "guardianNomination",
    status: !input.hasMinorChildren
      ? "na"
      : input.hasGuardianNomination
        ? "covered"
        : "gap",
    defaultIfMissing: DEFAULT_IF_MISSING.guardianNomination,
  });

  const gaps = items.filter((i) => i.status === "gap").length;
  const stale = items.filter((i) => i.status === "stale").length;
  const covered = items.filter((i) => i.status === "covered").length;
  const applicable = items.filter((i) => i.status !== "na").length;

  return {
    items,
    gaps,
    stale,
    covered,
    applicable,
    actionSuggested: gaps > 0 || stale > 0,
  };
}
